import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { cn } from '@/lib/utils';

export type ButtonVariant = 'default' | 'ghost' | 'outline' | 'destructive';
export type ButtonSize = 'sm' | 'default' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const VARIANTS: Record<ButtonVariant, string> = {
  // `btn-primary` es la firma de marca definida en globals.css (gradiente).
  default: 'btn-primary',
  ghost: 'bg-transparent text-body hover:bg-accent-sf hover:text-accent',
  outline:
    'border border-border bg-surface text-ink hover:bg-accent-sf hover:text-accent hover:border-accent-sf',
  destructive: 'bg-[color:var(--critical)] text-white hover:brightness-110 shadow-card',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-md',
  default: 'h-9 px-4 text-sm rounded-md',
  lg: 'h-10 px-5 text-sm rounded-lg',
  icon: 'h-9 w-9 rounded-md p-0 inline-flex items-center justify-center',
};

/**
 * Button — shadcn-style. Usa la clase `btn-primary` para el variant default
 * (gradiente de marca). Para los demas variants, estilos utilitarios Tailwind
 * con tokens del design system.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'default', type = 'button', ...props },
  ref,
) {
  // Para el variant default (btn-primary) no aplicamos clases de tamano
  // utilitarias, ya que btn-primary define padding/font propios. Pero si el
  // caller pasa size !== 'default', respetamos la altura.
  const isPrimary = variant === 'default';
  const sizeCls = isPrimary && size === 'default' ? '' : SIZES[size];

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-sf)]',
        VARIANTS[variant],
        sizeCls,
        className,
      )}
      {...props}
    />
  );
});

export default Button;
